import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./DetailCustomer.module.css";
import Header from "../../components/header/Header";
import PageHeading from "../../components/PageHeading/PageHeading";
import imageAvatar from "../../assets/images/avatar.svg";
import {
  User,
  Phone,
  Mail,
  Briefcase,
  MapPin,
  Globe,
  FileText,
  Hash,
  Award,
  Flag,
  Home,
  Map,
  Mail as MailBulk,
  CreditCard,
} from "react-feather";
import {
  useGetCustomerDetailsQuery,
  useUpdateUserStatusMutation,
} from "../../store/api/customerApi";
import { ICustomer } from "../../types";
import FilePreviewer from "../../components/FileViewer";
import { FaEye } from "react-icons/fa";
import ModePopup from "../../components/ModePopup";

const DetailCustomer: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"details" | "trade-references">(
    "details"
  );
  const [openGovIDModel, setOpenGovIDModel]=useState({
    url:"",
    field:""
  })
  const { id } = useParams<{ id: string }>();
  const userId = id ? parseInt(id, 10) : 1;
  const { data, isLoading } = useGetCustomerDetailsQuery(userId);
  const customerDetail = data?.data as ICustomer;
  const [updateUserStatus, { isLoading: isUpdating }] =
    useUpdateUserStatusMutation();
  const [localStatus, setLocalStatus] = useState<number | undefined>(
    customerDetail?.status
  );

  useEffect(() => {
    setLocalStatus(customerDetail?.status);
  }, [customerDetail?.status]);

  const handleStatusChange = async (status: number) => {
    try {
      await updateUserStatus({ userId, status }).unwrap();
      setLocalStatus(status);
    } catch (e) {
      // Optionally show error toast
    }
  };
 

  return (
    <div className={styles.container}>
      <Header />
      <div className={styles.mainContent}>
        <PageHeading title="Customer Details" backTo="/?tab=customers" />
        <div className={styles.content}>
          <div className={styles.leftPanel}>
            <div className={styles.OwnerDetailsWrapper}>
              {/* Row 1: Business Name, Phone, Email , Website*/}
              <div className={styles.ownerAvatarCard}>
                <div className={styles.avatarOwnerWrapper}>
                  <img
                    src={  imageAvatar}
                    alt="Owner Avatar"
                    className={styles.ownerAvatar}
                  />
                  <div className={styles.ownerName}>
                    {customerDetail?.owner_name}
                  </div>
                </div>
              </div>
              <div
                className={styles.OwnerDetailHorizontalDivider}
                style={{ margin: "0px" }}
              />
              <div className={styles.infoCard}>
                <span className={styles.infoIcon}>
                  <Phone />
                </span>
                <div className={styles.infoCol}>
                  <span className={styles.infoLabel}>Phone</span>
                  <div className={styles.infoValue}>
                    {customerDetail?.owner_phone}
                  </div>
                </div>
              </div>
              <div
                className={styles.horizontalDivider}
                style={{ margin: "0px" }}
              />
              <div className={styles.infoCard}>
                <span className={styles.infoIcon}>
                  <Mail />
                </span>

                <div className={styles.infoCol}>
                  <span className={styles.infoLabel}>Email</span>
                  <div className={styles.infoValue}>
                    {customerDetail?.owner_email}
                  </div>
                </div>
              </div>
              {/* Approve/Reject Buttons or Status */}
            </div>
            <div className={styles.actionButtonsWrapper}>
              {localStatus === 0 ? (
                <>
                  <button
                    className={styles.approveButton}
                    onClick={() => handleStatusChange(1)}
                    disabled={isUpdating}
                  >
                    Approve
                  </button>
                  <button
                    className={styles.rejectButton}
                    onClick={() => handleStatusChange(2)}
                    disabled={isUpdating}
                  >
                    Reject
                  </button>
                </>
              ) : localStatus === 1 ? (
                <div
                  className={`${styles.statusBadge} ${styles.statusApproved}`}
                >
                  Approved
                </div>
              ) : localStatus === 2 ? (
                <div
                  className={`${styles.statusBadge} ${styles.statusRejected}`}
                >
                  Rejected
                </div>
              ) : null}
            </div>
          </div>
          <div style={{ width: "100%" }}>
            {/* Tab bar above rightPanel, not inside white background */}
            <div className={styles.tabBar}>
              <button
                className={
                  activeTab === "details"
                    ? `${styles.tabButton} ${styles.activeTab}`
                    : styles.tabButton
                }
                onClick={() => setActiveTab("details")}
                type="button"
              >
                Details
              </button>
              <button
                className={
                  activeTab === "trade-references"
                    ? `${styles.tabButton} ${styles.activeTab}`
                    : styles.tabButton
                }
                onClick={() => setActiveTab("trade-references")}
                type="button"
              >
                Trade References
              </button>
            </div>
            <div className={styles.rightPanel}>
              {/* Tab Content */}
              {activeTab === "details" ? (
                <>
                  {/* Row 1: Business Name, Phone, Email , Website*/}
                  <div className={styles.colGroup}>
                    <div className={styles.infoCard}>
                      <span className={styles.infoIcon}>
                        <Briefcase />
                      </span>

                      <div className={styles.infoCol}>
                        <span className={styles.infoLabel}>Business Name</span>
                        <div className={styles.infoValue}>
                          {customerDetail?.business_name}
                        </div>
                      </div>
                    </div>
                    <div className={styles.infoCard}>
                      <span className={styles.infoIcon}>
                        <Phone />
                      </span>

                      <div className={styles.infoCol}>
                        <span className={styles.infoLabel}>Phone</span>
                        <div className={styles.infoValue}>
                          {customerDetail?.phone}
                        </div>
                      </div>
                    </div>
                    <div className={styles.infoCard}>
                      <span className={styles.infoIcon}>
                        <Mail />
                      </span>

                      <div className={styles.infoCol}>
                        <span className={styles.infoLabel}>Email</span>
                        <div className={styles.infoValue}>
                          {customerDetail?.email}
                        </div>
                      </div>
                    </div>
                    <div className={styles.infoCard}>
                      <span className={styles.infoIcon}>
                        <Globe />
                      </span>

                      <div className={styles.infoCol}>
                        <span className={styles.infoLabel}>Website</span>
                        <div className={styles.infoValue}>
                          <a
                            href={customerDetail?.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.portalUrl}
                          >
                            {customerDetail?.website_url}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.horizontalDivider} />
                  {/* Row 2: Address only */}
                  <div className={styles.colGroup}>
                    <div className={styles.infoCard}>
                      <span className={styles.infoIcon}>
                        <MapPin />
                      </span>
                      <div className={styles.infoCol}>
                        <span className={styles.infoLabel}>Address</span>
                        <div className={styles.infoValue}>
                          {`${customerDetail?.address}, ${customerDetail?.secondary_address} ${customerDetail?.building_name}`}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.horizontalDivider} />
                  {/* Row 3: City, State/Province, Country, Postal Code */}
                  <div className={styles.colGroup}>
                    <div className={styles.infoCard}>
                      <span className={styles.infoIcon}>
                        <Home />
                      </span>

                      <div className={styles.infoCol}>
                        <span className={styles.infoLabel}>City</span>
                        <div className={styles.infoValue}>
                          {customerDetail?.city}
                        </div>
                      </div>
                    </div>
                    <div className={styles.infoCard}>
                      <span className={styles.infoIcon}>
                        <MapPin />
                      </span>

                      <div className={styles.infoCol}>
                        <span className={styles.infoLabel}>State/Province</span>
                        <div className={styles.infoValue}>
                          {customerDetail?.state}
                        </div>
                      </div>
                    </div>
                    <div className={styles.infoCard}>
                      <span className={styles.infoIcon}>
                        <Flag />
                      </span>

                      <div className={styles.infoCol}>
                        <span className={styles.infoLabel}>Country</span>
                        <div className={styles.infoValue}>
                          {customerDetail?.country}
                        </div>
                      </div>
                    </div>
                    <div className={styles.infoCard}>
                      <span className={styles.infoIcon}>
                        <MailBulk />
                      </span>

                      <div className={styles.infoCol}>
                        <span className={styles.infoLabel}>Postal Code</span>
                        <div className={styles.infoValue}>
                          {customerDetail?.postal_code}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.horizontalDivider} />
                  {/* Row 4: Resale Certificate Number, Government License Number, Tax Identification Number */}
                  <div className={styles.colGroup}>
                    <div className={styles.infoCard}>
                      <span className={styles.infoIcon}>
                        <Award />
                      </span>

                      <div className={styles.infoCol}>
                        <span className={styles.infoLabel}>
                          Resale Certificate Number
                        </span>
                        <div className={styles.infoValue}>
                          {customerDetail?.resale_certificate_number}
                        </div>
                      </div>
                    </div>
                    <div className={styles.infoCard}>
                      <span className={styles.infoIcon}>
                        <CreditCard />
                      </span>

                      <div className={styles.infoCol}>
                        <span className={styles.infoLabel}>
                          Government License Number
                        </span>
                        <div className={styles.infoValue}>
                          {customerDetail?.gov_license_number}
                        </div>
                      </div>
                    </div>
                    <div className={styles.infoCard}>
                      <span className={styles.infoIcon}>
                        <Hash />
                      </span>

                      <div className={styles.infoCol}>
                        <span className={styles.infoLabel}>
                          Tax Identification Number / CRA GST Number
                        </span>
                        <div className={styles.infoValue}>
                          {customerDetail?.tin_gst_number}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.horizontalDivider} />
                  {/* Row 5: Valid Government Issued ID, Business License */}
                  <div className={styles.infoGrid}>
                    <div className={styles.infoCard}>
                      <span className={styles.infoIcon}>
                        <CreditCard />
                      </span>

                      <div className={styles.infoCol}>
                        <span className={styles.infoLabel}>
                          Valid Government Issued ID
                        </span>
                        <div className={styles.docPreview} onClick={()=>setOpenGovIDModel({field:'gov_id_url',url:customerDetail?.gov_id_url||""})}>
                          <FaEye/>
                           {/* <FilePreviewer url={customerDetail?.gov_id_url}/> */}
                           
                        </div>
                      </div>
                    </div>
                    <div className={styles.infoCard}>
                      <span className={styles.infoIcon}>
                        <FileText />
                      </span>

                      <div className={styles.infoCol}>
                        <span className={styles.infoLabel}>
                          Business License
                        </span>
                        <div className={styles.docPreview}  onClick={()=>setOpenGovIDModel({field:'biz_license_url',url:customerDetail?.biz_license_url||""})}>
                          <FaEye/>
                          {/* <FilePreviewer url={customerDetail?.biz_license_url}/> */}
                           
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Company Name</th>
                      <th>Person Name</th>
                      <th>Email</th>
                      <th>Contact Number</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerDetail?.trade_reference.map(
                      (ref: any, idx: any) => (
                        <tr key={idx}>
                          <td>{ref.company_name}</td>
                          <td>{ref.person_name}</td>
                          <td>{ref.person_email}</td>
                          <td>{ref.person_contact_number}</td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
      <ModePopup
        isOpen={Boolean(openGovIDModel.url)}
        mode="view"
        title="Document"
        onClose={() => setOpenGovIDModel({field:"",url:""})}
        onSubmit={() => {
           
          setOpenGovIDModel({field:"",url:""});
        }}
      >
         <div>
          <FilePreviewer url={openGovIDModel?.url}/>
         </div>
      </ModePopup>
    </div>
  );
};

export default DetailCustomer;
